import { useNavigate } from "react-router-dom";
import { Card, Form, Input, Button } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useLogin } from "./hooks/useLogin";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { ErrorNotification } from "@/components/common";

interface LoginFormValues {
  username: string;
  password: string;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const { showSuccess } = useErrorHandler();
  const { login: authLogin, isAuthenticated, user } = useAuth();
  const hasNavigated = useRef(false);
  const [loginError, setLoginError] = useState<any>(null);

  // Watch for authentication state changes and navigate accordingly
  useEffect(() => {
    if (isAuthenticated && user && !hasNavigated.current) {
      hasNavigated.current = true;

      if (user.roles.includes("worker")) {
        navigate("/expenses/record");
      } else {
        navigate("/overview");
      }
    }
  }, [isAuthenticated, user, navigate]);

  const loginMutation = useLogin({
    onSuccess: (data) => {
      showSuccess("Login successful!");

      const userData = {
        id: parseInt(data.id),
        username: data.name,
        email: data.email,
        roles: data.roles,
        permissions: data.permissions,
        is_active: data.is_active,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      authLogin(userData);
    },
    onError: (err: any) => {
      setLoginError(err);
    },
  });

  const onFinish = (values: LoginFormValues) => {
    setLoginError(null); // Clear any previous errors
    loginMutation.mutate(values);
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f0f2f5",
      }}
    >
      <Card
        title="Kraken Login"
        style={{
          width: 400,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Form name="login" onFinish={onFinish} layout="vertical">
          {loginError && (
            <ErrorNotification
              error={loginError}
              onClose={() => setLoginError(null)}
              showDetails={false}
            />
          )}
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Please input your username!" }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Username"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loginMutation.isPending}
              block
              size="large"
            >
              Log in
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;
